<template>
    <div>

        <Card :header="$t('traffic')" overlay :loading="loading">

            <ErrorMessage :value="requestChart.getError()" />

            <template #nav>
                <div :key="spaceId" class="d-flex gap-3">
                    <DropdownFilter v-model="currentChart" :items="['impressions', 'clicks']" :prefix="`${$t('type')}: `" :disabled="loading" />
                    <MultiselectAvailableCampaigns :disabled="loading" @changeTrackBy="campaigns = $event" @loading="campaignsLoading = $event" />
                    <FormCategoryTreeSelect
                        ref="FormCategoryTreeSelect"
                        :value="categories"
                        :disabled="loading"
                        class="width-200"
                        preselect
                        prevent-empty-selection
                        :clearable="false"
                        @change="categories = $event"
                        @loading="categoriesLoading = $event"
                    />
                </div>
            </template>

            <div class="d-flex gap-4 flex-column">
                <EChart
                    :options="chartOptions1"
                    type="time"
                    :header="$t('offersDisplay')"
                    card
                    no-padding
                    class="m-0 p-0 shadow-none"
                    :loading="requestChart.getLoading()"
                />
                <div class="d-flex gap-4 flex-wrap">
                    <EChart
                        :options="chartOptions2"
                        type="time"
                        :header="$t('percentageShare')"
                        card
                        no-padding
                        class="m-0 p-0 flex-basis-0 flex-grow-10 width-min-400 shadow-none"
                        :loading="requestChart.getLoading()"
                    />
                    <EChart
                        :options="chartOptions3"
                        :header="$t('percentageShareSum')"
                        card
                        no-padding
                        class="mb-0 width-min-500 flex-basis-0 flex-grow-1 shadow-none"
                        :loading="requestChart.getLoading()"
                    />
                </div>
            </div>

        </Card>
    </div>
</template>



<script>
    import { format } from 'date-fns';
    import { generateEChartData } from '@/utils/chart';
    import EChart from '@/components/common/EChart.vue';
    import MultiselectAvailableCampaigns from '@/components/common/MultiselectAvailableCampaigns.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'ImpressionsReport',
        components: {
            EChart,
            MultiselectAvailableCampaigns,
            FormCategoryTreeSelect,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestChartImpressions: this.$req('space/ImpressionsReport'),
                requestChartClicks: this.$req('space/ClicksReport'),
                campaigns: [],
                categories: [],
                campaignsLoading: false,
                categoriesLoading: false,
                formatDate: format,
                currentChart: 'impressions',
            };
        },
        computed: {
            requestChart() {
                if (this.currentChart === 'impressions') return this.requestChartImpressions;
                if (this.currentChart === 'clicks') return this.requestChartClicks;
                return null;
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate } = this.timeRange;
                const requestParams = { startDate, endDate };
                if (this.campaigns?.length) requestParams.campaigns = this.campaigns.join(',');
                if (this.categories?.length) requestParams.categories = this.categories.join(',');
                return { urlParams, requestParams };
            },
            loadingFilters() {
                return this.campaignsLoading || this.categoriesLoading;
            },
            loading() {
                return this.requestChart.getLoading() || this.loadingFilters;
            },
            chartData() {
                return this.requestChart.getData() || {};
            },
            chartSeries() {
                const chartData = (this.chartData?.chart || []).reduce((acc, item) => {
                    Object.keys(item).forEach(name => {
                        if (name !== 'date') {
                            if (!acc[name]) acc[name] = []; // eslint-disable-line no-param-reassign
                            acc[name].push([item.date, item[name]]);
                        }
                    });
                    return acc;
                }, {});
                return Object.keys(chartData).map(name => ({
                    name,
                    data: generateEChartData(this.timeRange, chartData[name]),
                }));
            },
            chartOptions1() {
                if (!this.chartSeries.length) {
                    return { series: [] };
                }
                const names = this.chartSeries.map(item => item.name);
                const series = this.chartSeries.map(item => ({
                    ...item,
                    type: 'line',
                }));
                return {
                    legend: {
                        data: names,
                    },
                    yAxis: {
                        type: 'value',
                    },
                    series,
                };
            },
            chartOptions2() {
                if (!this.chartSeries.length) {
                    return { series: [] };
                }
                const pivotSeries = (series, filterZeros = false) => {
                    /* eslint-disable no-param-reassign */
                    // 1) Zbierz wszystkie rekordy w Map<timestamp, record>
                    const dateMap = new Map();
                    for (const { name, data } of series) {
                        for (const [ts, val] of data) {
                            const rec = dateMap.get(ts) ?? { date: ts };
                            rec[name] = val;
                            dateMap.set(ts, rec);
                        }
                    }
                    // 2) Upewnij się, że każdy record ma wszystkie nazwy (domyślnie 0)
                    const names = series.map(s => s.name);
                    const pivoted = [...dateMap.values()].map(rec => {
                        for (const name of names) {
                            rec[name] = rec[name] ?? 0;
                        }
                        return rec;
                    });
                    // 3) (opcjonalnie) odfiltruj rekordy z samymi zerami
                    return filterZeros ? pivoted.filter(rec => names.some(name => rec[name] !== 0)) : pivoted;
                    /* eslint-enable no-param-reassign */
                };
                const rawData = pivotSeries(this.chartSeries, false);
                const names = this.chartSeries.map(item => item.name);
                return {
                    legend: {
                        data: names,
                    },
                    dataset: [
                        { source: rawData },
                        {
                            transform: {
                                type: 'percent',
                                config: {
                                    dimension: names,
                                },
                            },
                        },
                    ],
                    series: names.map(name => ({
                        name,
                        type: 'bar',
                        stack: 'total',
                        datasetIndex: 1,
                        encode: {
                            x: 'date',
                            y: name,
                        },
                    })),
                };
            },
            chartOptions3() {
                const chartData = this.chartData.pie_chart;
                if (!chartData) {
                    return { series: [] };
                }
                return {
                    series: [
                        {
                            type: 'pie',
                            data: chartData.map(([name, value]) => ({ name, value })),
                        },
                    ],
                };
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
            currentChart: {
                handler: 'loadData',
            },
        },
        methods: {
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.requestChart.abortAll();
                this.requestChart.resetAll();
            },
            fetchData() {
                this.requestChart.get(this.apiParams);
            },
            resetFilters() {
                this.campaigns = [];
                this.categories = [];
            },
        },
    };
</script>
